import { useState, useRef, useEffect } from "react";
import Button from "../../components/ui/Button";
import { useTheme } from "../../contexts/ThemeContext";
import { validateOTP } from "../../utils/validations";
import { apiCall } from "../../api/fetchClient";
import { SEND_OTP_URL, VERIFY_OTP_URL } from "../../api/url";
import { strings } from "../../strings/strings";
import { HiArrowRight, HiArrowLeft, HiOutlineShieldCheck } from "react-icons/hi";

const OtpVerification = ({ email, onSuccess }) => {
  const { isDark } = useTheme();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);

  useEffect(() => {
    if (timer > 0) {
      const id = setInterval(() => setTimer((p) => p - 1), 1000);
      return () => clearInterval(id);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const handleChange = (index, value) => {
    if (isNaN(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setError("");
    if (value !== "" && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const next = [...pasted.split(""), ...Array(6 - pasted.length).fill("")];
    setOtp(next);
    setError("");
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    const otpResult = validateOTP(otpValue);
    if (!otpResult.isValid) {
      setError(otpResult.message);
      return;
    }

    setIsVerifying(true);
    setError("");
    try {
      const response = await apiCall(VERIFY_OTP_URL, "POST", { email, otp: otpValue });
      if (!!response?.success) onSuccess(response?.results);
    } catch (err) {
      setError(err.message || strings.ERROR_INVALID_OTP);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!canResend || isResending) return;
    setIsResending(true);
    try {
      const response = await apiCall(SEND_OTP_URL, "POST", { email });
      if (!!response?.success) {
        setTimer(60);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
        setError("");
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError(err.message || strings.ERROR_RESEND_OTP);
    } finally {
      setIsResending(false);
    }
  };

  const isComplete = otp.join("").length === 6;

  return (
    <div className="animate-slideUp w-full text-center">
      <form onSubmit={handleSubmit} noValidate className="space-y-6">
        {/* Top Centered Shield Icon & Headings */}
        <div className="flex flex-col items-center">
          {/* Shield Icon Container */}
          <div className="w-16 h-16 rounded-[22px] bg-gradient-to-br from-primary to-[#00B4A6] flex items-center justify-center shadow-lg shadow-primary/20 mb-5 transform hover:scale-105 transition-transform duration-300">
            <HiOutlineShieldCheck className="w-8 h-8 text-white stroke-[1.5]" />
          </div>

          <h2
            className={`text-xl sm:text-2xl font-black tracking-wider uppercase mb-1.5 ${
              isDark ? "text-white" : "text-slate-905 font-extrabold"
            }`}
          >
            {strings.VERIFY_OTP_TITLE}
          </h2>

          <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"} mb-0.5`}>
            {strings.VERIFY_OTP_SUBTITLE}
          </p>

          <p className="text-sm font-extrabold tracking-wide text-primary break-all mb-2">{email}</p>
        </div>

        {/* Error Notification */}
        {error && (
          <div
            className={`px-4 py-3 rounded-2xl border text-xs font-semibold animate-fadeIn flex items-center justify-center gap-2 ${
              isDark
                ? "bg-red-500/10 border-red-500/20 text-red-400"
                : "bg-red-50 border-red-100 text-red-650 shadow-sm shadow-red-50/50"
            }`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0 animate-pulse" />
            {error}
          </div>
        )}

        {/* OTP Input Grid */}
        <div className="grid grid-cols-6 gap-2 sm:gap-3.5 max-w-sm mx-auto">
          {otp.map((digit, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onPaste={handlePaste}
              autoFocus={i === 0}
              className={`
                w-full aspect-[4/5] sm:h-16 h-14
                text-center text-xl font-bold
                border outline-none transition-all duration-300 rounded-2xl
                ${
                  error
                    ? isDark
                      ? "border-red-500 bg-red-500/5 text-red-400 focus:ring-4 focus:ring-red-500/20"
                      : "border-red-400 bg-red-50 text-red-655 focus:ring-4 focus:ring-red-500/20"
                    : isDark
                      ? digit
                        ? "border-primary bg-slate-900/80 text-white focus:ring-4 focus:ring-primary/20"
                        : "border-slate-800 bg-slate-900/40 text-white focus:border-primary focus:bg-slate-900 focus:ring-4 focus:ring-primary/20 hover:border-slate-700"
                      : digit
                        ? "border-primary bg-white text-slate-900 focus:ring-4 focus:ring-primary/20 shadow-sm"
                        : "border-slate-200 bg-white text-slate-900 focus:border-primary focus:ring-4 focus:ring-primary/20 shadow-sm hover:border-slate-300"
                }
              `}
            />
          ))}
        </div>

        {/* Resend Timer */}
        <div className="text-center pt-1">
          {!canResend ? (
            <p
              className={`text-[11px] font-black tracking-widest uppercase ${
                isDark ? "text-slate-500" : "text-slate-400"
              }`}
            >
              {strings.RESEND_OTP_TEXT} <span className="text-primary font-black tabular-nums">{timer}S</span>
            </p>
          ) : (
            <p className={`text-xs font-semibold ${isDark ? "text-slate-500" : "text-slate-400"}`}>
              {strings.DIDNT_RECEIVE_CODE}{" "}
              <button
                type="button"
                onClick={handleResend}
                disabled={isResending}
                className={`font-bold transition-colors duration-150 ${
                  isDark ? "text-primary hover:text-primary-dark" : "text-primary-dark hover:text-primary"
                } underline underline-offset-2`}
              >
                {isResending ? strings.SENDING_TEXT : strings.RESEND_CODE}
              </button>
            </p>
          )}
        </div>

        {/* Verify Button */}
        <Button
          type="submit"
          variant="primary"
          fullWidth
          size="lg"
          loading={isVerifying}
          disabled={!isComplete || isVerifying}
          className={`group relative overflow-hidden rounded-2xl font-bold tracking-widest uppercase text-xs sm:text-sm py-4 shadow-lg transition-all duration-300 hover:brightness-105 active:scale-[0.98] ${
            isDark
              ? "bg-gradient-to-r from-primary to-[#00B4A6] text-slate-950 shadow-primary/10 disabled:opacity-50 disabled:shadow-none"
              : "bg-gradient-to-r from-primary to-[#00B4A6] text-white shadow-primary/25 disabled:opacity-50 disabled:shadow-none"
          }`}
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            {strings.VERIFY_LOGIN_TEXT}
            {!isVerifying && (
              <HiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300 stroke-[2]" />
            )}
          </span>
        </Button>

        {/* Back to Email */}
        <div className="flex justify-center pt-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className={`flex items-center gap-2 text-xs font-black tracking-wider uppercase transition-colors duration-150 ${
              isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <HiArrowLeft className="w-4 h-4 stroke-[2]" />
            {strings.BACK_TO_EMAIL}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OtpVerification;
